// The urls this console talks to.
//
// BOTH HALVES ARE NOW IMPORTED FROM THE SERVICE, and neither is restated.
//
// api/src/auth/endpoints.ts imports nothing at all — it was split out precisely
// so that naming a url costs no dependencies, on an argument about an import
// cycle between the rate limiter and the routes — and that same property is what
// makes it safe to pull into a browser bundle.
//
// The report urls used to be a copy kept here, because REPORT_ENDPOINTS sat in
// api/src/reports/routes.ts beside `import type { FastifyInstance }`, the
// operator package and the database module, and importing it would have pulled
// Fastify and Postgres into the bundle. The interim was a parity check that read
// the service's source text and failed the build if the two disagreed. It worked,
// and it existed only because the import was impossible.
//
// It is possible now: 2026-08-28 split api/src/reports/endpoints.ts out on the
// same argument, so the copy and the check that guarded it are both gone. One
// constant, two readers, and no way for the console to ask for a url the service
// does not answer on — which is a stronger guarantee than any comparison of two
// spellings, because there is only one spelling.

// SESSION_TOKEN_HEADER travels the same way, and for a sharper reason than the
// urls did. A url that disagrees with the service produces a 404 on the first
// request, which is loud. A response header name that disagrees produces
// nothing: `headers.get` answers null, the console adopts no replacement, and
// every rotating session is revoked for reuse a grace window later with no error
// anywhere. There was never going to be a hand-kept copy of this one.
// FRESH_FACTOR_FIELD travels for the same reason SESSION_TOKEN_HEADER does, and
// it fails the same quiet way. It is the body field the six digits arrive in,
// and a console that spells it differently from the service does not get an
// error naming the field — it gets `second_factor_code_required` on every
// request, which reads exactly like a person who forgot to type their code. It
// was `totp` here for the length of one edit, and that is how it was found.
export { AUTH_ENDPOINTS, FRESH_FACTOR_FIELD, SESSION_TOKEN_HEADER } from "../../../api/src/auth/endpoints.js";
export {
  ACTIVATIONS_ENDPOINT,
  ACTIVITY_ENDPOINT,
  ATTENDEE_ENDPOINTS,
  EXPORT_HISTORY_ENDPOINT,
  REPORT_ENDPOINTS,
  UNVERIFIED_ATTEMPTS_ENDPOINT,
} from "../../../api/src/reports/endpoints.js";
export { RIDE_ENDPOINTS } from "../../../api/src/ride/endpoints.js";

import { RIDE_ENDPOINTS } from "../../../api/src/ride/endpoints.js";
import {
  ACTIVITY_ENDPOINT,
  ATTENDEE_ENDPOINTS,
  EXPORT_HISTORY_ENDPOINT,
  REPORT_ENDPOINTS,
  UNVERIFIED_ATTEMPTS_ENDPOINT,
} from "../../../api/src/reports/endpoints.js";

export type ReportName = keyof typeof REPORT_ENDPOINTS;

/**
 * Which verb each report answers on.
 *
 * THE ONE THING ABOUT THE REPORTS THE SERVICE DOES NOT EXPORT, so this is a
 * restatement — the shape the url copy used to have, and which the header above
 * celebrates being rid of. It is here because the alternative is worse rather
 * than because it is good: reconciliation is a POST, deliberately, since it
 * takes the crew's device roster and "a roster smuggled through a query string
 * is a roster people stop supplying". A console that assumed GET for everything
 * would ask for that report with the wrong verb and read Fastify's 404 as a
 * report that does not exist.
 *
 * What makes the restatement safe is that check.mjs reads the registrations out
 * of api/src/reports/routes.ts's own source text and compares them to this
 * object in both directions, so a report that changes verb — or a fifth one
 * added there — fails the build rather than a request. That is the same
 * mechanism the urls had before they could be imported, and it should be
 * deleted the day the service names its verbs somewhere a browser can read
 * them. It is written up in xp/CONSOLE-USABLE-REPORT.md as a server change
 * worth making.
 */
export const REPORT_METHODS: Readonly<Record<ReportName, "GET" | "POST">> = {
  operator: "GET",
  export: "GET",
  reconciliation: "POST",
  optIn: "GET",
  survey: "GET",
};

/**
 * A report url for one activation.
 *
 * The id is encoded rather than interpolated raw. It arrives from the path of
 * the page, which is to say from whatever somebody typed or pasted, and a value
 * containing a slash or a question mark would otherwise change which endpoint is
 * being called rather than which activation is being asked about.
 */
export function reportUrl(report: ReportName, activationId: string): string {
  return REPORT_ENDPOINTS[report].replace(":activationId", encodeURIComponent(activationId));
}

/**
 * The export url for one activation and one purpose.
 *
 * THE PURPOSE IS A REQUIRED ARGUMENT OF THIS FUNCTION, and that is the whole of
 * why it exists rather than being a caller appending a query string. The service
 * defaults an absent `purpose` to `participation_report`, which is the widest
 * file it can produce; that default is right for a caller that has been reading
 * exportPurpose.ts, and it is wrong for a console, where a request that forgot
 * to name a purpose would hand somebody every column of every attendee and look
 * exactly like a request that asked for it. There is no way to call this without
 * saying what the file is for.
 */
export function exportUrl(activationId: string, purpose: string): string {
  return `${reportUrl("export", activationId)}?purpose=${encodeURIComponent(purpose)}`;
}

/**
 * The export history url for one activation.
 *
 * NOT A MEMBER OF REPORT_METHODS AND NOT REACHED THROUGH `reportUrl`, which is
 * the same shape `exportUrl` has and for a related reason. That record is five
 * named reports, each of which files a `report_access` row; this reads those
 * rows back and files nothing. Putting it in that object would have made it the
 * sixth report to every exhaustive switch over `ReportName` in this console and
 * in its check — including the verb table above, whose whole justification is
 * that it is a restatement of five registrations somebody can compare by eye.
 *
 * Encoded rather than interpolated, for the reason `reportUrl` gives: the id
 * arrives from the path of the page, which is to say from whatever somebody
 * typed or pasted.
 */
export function exportHistoryUrl(activationId: string): string {
  return EXPORT_HISTORY_ENDPOINT.replace(":activationId", encodeURIComponent(activationId));
}

/**
 * One page of the people who registered at an activation.
 *
 * NOT A MEMBER OF REPORT_METHODS AND NOT REACHED THROUGH `reportUrl`, on the
 * same footing as the export history above: `ReportName` is the five audited
 * reports, and reports/endpoints.ts keeps these two in a sibling object
 * precisely so that adding them does not make the console's exhaustive switch
 * demand a sixth report screen.
 *
 * BOTH BOUNDS ARE ALWAYS ON THE WIRE, WHICH IS `exportUrl`'S RULE APPLIED TO A
 * DIFFERENT DEFAULT. The service answers an absent `limit` with fifty, and that
 * default is right for a caller who has read attendees.ts and wrong for a
 * console: `report_access.row_count` is how much personal data left, the page is
 * the unit that record counts in, and a request that left the size to the
 * service would be a console that does not know how big its own recorded
 * disclosure was. So the pager's numbers go out, every time, and the console
 * never leans on the default being the small one.
 *
 * The service refuses an empty parameter rather than reading it as no
 * preference, so these are written from numbers and never from a text box.
 */
export function attendeesUrl(
  activationId: string,
  limit: number,
  offset: number,
  order: ListOrderAsk | null = null,
): string {
  const base = ATTENDEE_ENDPOINTS.list.replace(":activationId", encodeURIComponent(activationId));
  const page = `limit=${encodeURIComponent(String(limit))}&offset=${encodeURIComponent(String(offset))}`;
  return `${base}?${[page, orderQuery(order)].filter(Boolean).join("&")}`;
}

/**
 * A list's order as the service takes it (12 Oct hand-off: every column sorts,
 * on the server). The key is one of the service's own constants, typed by the
 * caller against the list it reads.
 */
export type ListOrderAsk = { readonly key: string; readonly dir: "asc" | "desc" };

/**
 * `sort=<key>&dir=<asc|desc>`, or nothing at all for the service's own order.
 *
 * NOTHING RATHER THAN THE DEFAULT SPELLED OUT, which is desk-ui's `sortParams`
 * rule: a list nobody has sorted asks exactly what it asked before sorting
 * existed, so its first read is the plain one and the server stays the one
 * place that knows what its own order is.
 */
export function orderQuery(order: ListOrderAsk | null): string {
  if (order === null) return "";
  return `sort=${encodeURIComponent(order.key)}&dir=${order.dir === "desc" ? "desc" : "asc"}`;
}

/**
 * One person's record.
 *
 * The reference is encoded for the reason `reportUrl` gives about an activation
 * id, and with more force: this segment arrives from a row the service sent, and
 * an unencoded value carrying a slash would ask a different endpoint about a
 * different thing entirely.
 */
export function attendeeUrl(activationId: string, attendeeRef: string): string {
  return ATTENDEE_ENDPOINTS.record
    .replace(":activationId", encodeURIComponent(activationId))
    .replace(":attendeeRef", encodeURIComponent(attendeeRef));
}

/**
 * One page of the addresses that were typed at an activation and never proved.
 *
 * A THIRD SIBLING OF REPORT_METHODS, and the service put it there on purpose:
 * reports/endpoints.ts keeps this constant out of REPORT_ENDPOINTS with a
 * comment saying so, because a sixth member of that object is a sixth arm of
 * every exhaustive switch over `ReportName` in this console. The two functions
 * above have the same shape for the same reason, and this one joins them rather
 * than starting a new pattern.
 *
 * BOTH BOUNDS ARE ALWAYS ON THE WIRE, which is `attendeesUrl`'s rule and it
 * binds harder here. The service defaults an absent `limit` to fifty and caps it
 * at two hundred; fifty addresses of people who never agreed to be written down
 * is a larger disclosure than fifty attendees, and the audit row the service
 * writes counts exactly what it sent. A console that left the size off would be
 * asking for a number it did not choose and then not knowing what it had asked
 * for. So the pager's numbers go out every time.
 *
 * Encoded rather than interpolated, for the reason `reportUrl` gives: the id
 * arrives from the path of the page, which is to say from whatever somebody
 * typed or pasted.
 */
export function unverifiedAttemptsUrl(
  activationId: string,
  limit: number,
  offset: number,
  /** The addresses' order (`sort`) and the domain tally's (`domains_sort`), each null for the service's own. */
  order: ListOrderAsk | null = null,
  domainOrder: ListOrderAsk | null = null,
): string {
  const base = UNVERIFIED_ATTEMPTS_ENDPOINT.replace(":activationId", encodeURIComponent(activationId));
  const query = [`limit=${encodeURIComponent(String(limit))}&offset=${encodeURIComponent(String(offset))}`, orderQuery(order)];
  if (domainOrder !== null) {
    query.push(`domains_sort=${encodeURIComponent(domainOrder.key)}&domains_dir=${domainOrder.dir === "desc" ? "desc" : "asc"}`);
  }
  return `${base}?${query.filter(Boolean).join("&")}`;
}

/**
 * One page of what happened at an activation, newest first.
 *
 * A FOURTH SIBLING OF REPORT_METHODS, and reports/endpoints.ts says in its own
 * comment that this one is provisional where the three above it are settled: it
 * is a screen of its own and belongs in REPORT_ENDPOINTS the day the console has
 * one, and it is kept out today only because a sixth member of that object is a
 * sixth arm of every exhaustive switch over `ReportName`. The move, when
 * somebody makes it, is one line there and one arm in App.tsx. Nothing else
 * follows it: the audit name, the check constraint and the rate limit rule are
 * all keyed on this constant's value rather than on where it is declared.
 *
 * `limit` IS ALWAYS ON THE WIRE AND THE OTHER THREE ARE OMITTED WHEN ABSENT,
 * which is two rules rather than an inconsistency.
 *
 * The bound goes out every time, on `attendeesUrl`'s reasoning: the service
 * defaults an absent `limit` to fifty, `report_access.row_count` is how many
 * recorded acts this page put in front of somebody, and a console that left the
 * size off would not know how big the disclosure filed against the sign-in was.
 *
 * The cursor and the two filters are omitted rather than sent empty, because an
 * absent one is what the service reads as "from the newest row" and "everything"
 * respectively. `oneOf` and `parseCursor` both treat the empty string as absent
 * as well, so `action=` would work today; it would also be this console
 * asserting a narrowing it does not have, in a url that ends up in a support
 * question about why a feed looked short.
 *
 * Encoded rather than interpolated, for the reason `reportUrl` gives about the
 * activation id, and with one more of its own: a cursor is
 * `<timestamp> <activity_id>` with a space in the middle of it, so this is the
 * one url on this console where the unencoded form would not survive the wire.
 */
export function activityUrl(
  activationId: string,
  limit: number,
  filters: { cursor?: string | null; action?: string | null; entity?: string | null } = {},
  order: ListOrderAsk | null = null,
): string {
  const base = ACTIVITY_ENDPOINT.replace(":activationId", encodeURIComponent(activationId));
  const query = [`limit=${encodeURIComponent(String(limit))}`];
  if (filters.cursor !== undefined && filters.cursor !== null && filters.cursor !== "") {
    query.push(`cursor=${encodeURIComponent(filters.cursor)}`);
  }
  if (filters.action !== undefined && filters.action !== null && filters.action !== "") {
    query.push(`action=${encodeURIComponent(filters.action)}`);
  }
  if (filters.entity !== undefined && filters.entity !== null && filters.entity !== "") {
    query.push(`entity=${encodeURIComponent(filters.entity)}`);
  }
  const ordered = orderQuery(order);
  if (ordered !== "") query.push(ordered);
  return `${base}?${query.join("&")}`;
}

export function rideRidersUrl(activationId: string, order: ListOrderAsk | null = null): string {
  const base = RIDE_ENDPOINTS.listRiders.replace(":activationId", encodeURIComponent(activationId));
  const ordered = orderQuery(order);
  return ordered === "" ? base : `${base}?${ordered}`;
}

export function rideRiderUrl(activationId: string, riderId: string): string {
  return RIDE_ENDPOINTS.readRider
    .replace(":activationId", encodeURIComponent(activationId))
    .replace(":riderId", encodeURIComponent(riderId));
}

/**
 * The tape. Two tables on one answer, so two orders: `sort` for the runs and
 * `codes_sort` for the finish codes. Neither is sent until somebody asks.
 */
export function rideActivityUrl(
  activationId: string,
  runs: ListOrderAsk | null = null,
  codes: ListOrderAsk | null = null,
): string {
  const base = RIDE_ENDPOINTS.listActivity.replace(":activationId", encodeURIComponent(activationId));
  const query = [
    orderQuery(runs),
    codes === null ? "" : `codes_sort=${encodeURIComponent(codes.key)}&codes_dir=${codes.dir === "desc" ? "desc" : "asc"}`,
  ].filter(Boolean);
  return query.length === 0 ? base : `${base}?${query.join("&")}`;
}

/**
 * The verified board, placed by the service over a window (12 Oct hand-off:
 * the board is ordered where it is placed). The window always goes out, so the
 * read says which board it is; the days only for Custom dates, and only the
 * ones somebody typed.
 */
export function ridePlacesUrl(
  activationId: string,
  window: { readonly kind: "allTime" | "monthly" | "weekly" | "custom"; readonly from: string; readonly until: string },
  order: ListOrderAsk | null = null,
): string {
  const base = RIDE_ENDPOINTS.places.replace(":activationId", encodeURIComponent(activationId));
  const query = [`window=${encodeURIComponent(window.kind)}`];
  if (window.kind === "custom" && window.from !== "") query.push(`from=${encodeURIComponent(window.from)}`);
  if (window.kind === "custom" && window.until !== "") query.push(`until=${encodeURIComponent(window.until)}`);
  const ordered = orderQuery(order);
  if (ordered !== "") query.push(ordered);
  return `${base}?${query.join("&")}`;
}

export function rideMailUrl(activationId: string): string {
  return RIDE_ENDPOINTS.mail.replace(":activationId", encodeURIComponent(activationId));
}

export function rideBoardUrl(
  activationId: string,
  scope: "today" | "monthly" | "allTime",
  order: ListOrderAsk | null = null,
): string {
  const ordered = orderQuery(order);
  return `${RIDE_ENDPOINTS.board.replace(":activationId", encodeURIComponent(activationId))}?scope=${encodeURIComponent(scope)}${ordered === "" ? "" : `&${ordered}`}`;
}
